import React, { useState, useEffect, useRef } from 'react';

import profileImage from './images/profile.jpg'
import pythonIcon from './Icons/python.png';
import djangoIcon from './Icons/django.png';
import javaIcon from './Icons/java.png';
import htmlCssIcon from './Icons/html-css.png';
import javascriptIcon from './Icons/java-script.png';
import reactIcon from './Icons/react.png';
import arduinoIcon from './Icons/arduino.png';
import androidIcon from './Icons/android.png';
import githubIcon from './Icons/github.png';
import linkedinIcon from './Icons/linkedin.png';


// ---------------------------------------------------------------------------
// Circuit-trace background: a signature element built around the two worlds
// this resume bridges — backend/data (signal pulses) and hardware (PCB traces).
// Pure canvas, no dependencies, respects prefers-reduced-motion.
// ---------------------------------------------------------------------------
const CircuitBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let traces = [];
    let pulses = [];
    let animationId;
    let lastPulseSpawn = 0;

    const GRID = 64; // spacing between potential trace nodes

    function resize() {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      buildTraces();
    }

    // Build a set of orthogonal PCB-style traces (horizontal/vertical segments
    // with occasional right-angle turns), scattered across the viewport.
    function buildTraces() {
      traces = [];
      const cols = Math.ceil(canvas.offsetWidth / GRID) + 1;
      const rows = Math.ceil(canvas.offsetHeight / GRID) + 1;
      const traceCount = Math.max(14, Math.floor((cols * rows) / 18));

      for (let i = 0; i < traceCount; i++) {
        const startCol = Math.floor(Math.random() * cols);
        const startRow = Math.floor(Math.random() * rows);
        const segments = 2 + Math.floor(Math.random() * 3);
        const points = [{ col: startCol, row: startRow }];

        let curCol = startCol;
        let curRow = startRow;
        let horizontal = Math.random() > 0.5;

        for (let s = 0; s < segments; s++) {
          const dist = 1 + Math.floor(Math.random() * 4);
          if (horizontal) {
            curCol += Math.random() > 0.5 ? dist : -dist;
          } else {
            curRow += Math.random() > 0.5 ? dist : -dist;
          }
          points.push({ col: curCol, row: curRow });
          horizontal = !horizontal;
        }

        traces.push({
          points: points.map(p => ({ x: p.col * GRID, y: p.row * GRID })),
          opacity: 0.08 + Math.random() * 0.1,
        });
      }
    }

    function pathLength(points) {
      let len = 0;
      for (let i = 1; i < points.length; i++) {
        len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
      }
      return len;
    }

    function pointAtDistance(points, dist) {
      let remaining = dist;
      for (let i = 1; i < points.length; i++) {
        const segLen = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
        if (remaining <= segLen) {
          const t = segLen === 0 ? 0 : remaining / segLen;
          return {
            x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
            y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
          };
        }
        remaining -= segLen;
      }
      return points[points.length - 1];
    }

    function spawnPulse() {
      if (traces.length === 0) return;
      const trace = traces[Math.floor(Math.random() * traces.length)];
      pulses.push({
        trace,
        dist: 0,
        length: pathLength(trace.points),
        speed: 60 + Math.random() * 60, // px/sec
      });
    }

    function draw(timestamp) {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // static traces
      traces.forEach(trace => {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(94, 234, 212, ${trace.opacity})`;
        ctx.lineWidth = 1;
        trace.points.forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();

        // node dots at turns
        trace.points.forEach(p => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(94, 234, 212, ${trace.opacity + 0.05})`;
          ctx.fill();
        });
      });

      // moving pulses (light traveling along traces)
      if (!prefersReduced) {
        if (timestamp - lastPulseSpawn > 900 && pulses.length < 10) {
          spawnPulse();
          lastPulseSpawn = timestamp;
        }

        pulses.forEach(pulse => {
          const pos = pointAtDistance(pulse.trace.points, pulse.dist);
          const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, 8);
          gradient.addColorStop(0, 'rgba(94, 234, 212, 0.9)');
          gradient.addColorStop(1, 'rgba(94, 234, 212, 0)');
          ctx.beginPath();
          ctx.fillStyle = gradient;
          ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2);
          ctx.fill();

          ctx.beginPath();
          ctx.arc(pos.x, pos.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(226, 255, 250, 0.95)';
          ctx.fill();
        });

        pulses = pulses.filter(pulse => {
          pulse.dist += pulse.speed / 60;
          return pulse.dist < pulse.length;
        });
      }

      animationId = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    animationId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return <canvas ref={canvasRef} className="circuit-canvas" aria-hidden="true" />;
};

// Custom hook for scroll animations
const useScrollAnimation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Typing effect component
const TypingEffect = ({ text, speed = 100 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, speed]);

  return (
    <h1 className="typing-text">
      {displayText}
      <span className="cursor">_</span>
    </h1>
  );
};

// Section wrapper with scroll animation
const AnimatedSection = ({ children, delay = 0, label }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`animated-section ${isVisible ? 'visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {label && <span className="section-eyebrow">{label}</span>}
      {children}
    </div>
  );
};

export default function Resume() {
  const skills = [
    { name: 'Python', level: 90, icon: pythonIcon },
    { name: 'Django and Rest Framework', level: 90, icon: djangoIcon },
    { name: 'Java OOP', level: 95, icon: javaIcon },
    { name: 'HTML & CSS', level: 70, icon: htmlCssIcon },
    { name: 'JavaScript', level: 60, icon: javascriptIcon },
    { name: 'React.js', level: 40, icon: reactIcon },
    { name: 'Arduino Programming', level: 95, icon: arduinoIcon },
    { name: 'Android Java', level: 40, icon: androidIcon },
  ];

  const hobbies = [
    '⚡ Building IoT projects',
    '💻 Coding & experimenting',
    '🏛️ History',
    '🎮 Gaming',
    '🔧 DIY Electronics',
    '📖 Learning new tech',
  ];

  return (
    <div className="resume-container">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --ink: #0B1120;
          --ink-soft: #121A2E;
          --panel: #101827;
          --signal: #5EEAD4;
          --signal-soft: rgba(94, 234, 212, 0.15);
          --amber: #F5B971;
          --text: #E7ECF5;
          --text-dim: #8B97AC;
        }

        body {
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: var(--ink);
          color: var(--text);
          line-height: 1.6;
          overflow-x: hidden;
        }

        .resume-container {
          min-height: 100vh;
          background: var(--ink);
        }

        /* Header Styles */
        .header {
          text-align: center;
          padding: 80px 20px 70px;
          background: var(--ink);
          color: var(--text);
          position: relative;
          overflow: hidden;
          border-bottom: 1px solid rgba(94, 234, 212, 0.12);
        }

        .circuit-canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: block;
        }

        .header-blog-link {
          position: absolute;
          top: 24px;
          right: 28px;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 10px 16px;
          color: var(--signal);
          background: rgba(11, 17, 32, 0.78);
          border: 1px solid rgba(94, 234, 212, 0.45);
          border-radius: 6px;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.82rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-decoration: none;
          backdrop-filter: blur(8px);
          transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;
        }

        .header-blog-link:hover {
          transform: translateY(-2px);
          background: var(--signal);
          color: var(--ink);
        }

        .header-blog-link:focus-visible {
          outline: 2px solid var(--amber);
          outline-offset: 3px;
        }

        .profile-img {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          border: 2px solid var(--signal);
          object-fit: cover;
          animation: fadeInScale 1s ease-out;
          box-shadow: 0 0 0 8px rgba(94, 234, 212, 0.08), 0 20px 40px rgba(0,0,0,0.4);
          position: relative;
          z-index: 1;
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .typing-text {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 2.1rem;
          font-weight: 600;
          margin: 28px 0 10px;
          position: relative;
          z-index: 1;
          letter-spacing: -0.01em;
        }

        .cursor {
          color: var(--signal);
          animation: blink 0.9s steps(1) infinite;
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        .subtitle {
          font-size: 1rem;
          color: var(--signal);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          opacity: 0.85;
          position: relative;
          z-index: 1;
        }

        /* Content Styles */
        .content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 60px 24px 40px;
          background: var(--ink);
        }

        .animated-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          margin-bottom: 64px;
        }

        .animated-section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .section-eyebrow {
          display: block;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.75rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--signal);
          opacity: 0.7;
          margin-bottom: 8px;
        }

        .section-title {
          font-size: 1.7rem;
          font-weight: 700;
          margin-bottom: 24px;
          color: var(--text);
          display: inline-block;
        }

        .about-text {
          font-size: 1.05rem;
          line-height: 1.85;
          color: var(--text-dim);
          max-width: 65ch;
        }

        /* Skills Section */
        .skills-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
          margin-top: 8px;
        }

        .skill-item {
          background: var(--panel);
          padding: 20px;
          border-radius: 8px;
          border: 1px solid rgba(94, 234, 212, 0.1);
          transition: transform 0.25s ease, border-color 0.25s ease;
        }

        .skill-item:hover {
          transform: translateY(-3px);
          border-color: rgba(94, 234, 212, 0.4);
        }

        .skill-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .skill-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
          object-fit: contain;
        }

        .skill-name {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text);
        }

        .progress-bar {
          width: 100%;
          height: 5px;
          background: rgba(255,255,255,0.06);
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: var(--signal);
          border-radius: 3px;
          transition: width 1.2s cubic-bezier(0.16, 1, 0.3, 1);
        }

        /* Hobbies Section */
        .hobbies-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          list-style: none;
          margin-top: 8px;
        }

        .hobby-item {
          background: var(--panel);
          border: 1px solid rgba(94, 234, 212, 0.1);
          padding: 14px 18px;
          border-radius: 8px;
          font-size: 0.95rem;
          color: var(--text-dim);
          transition: transform 0.25s ease, border-color 0.25s ease;
        }

        .hobby-item:hover {
          transform: translateX(6px);
          border-color: rgba(94, 234, 212, 0.35);
          color: var(--text);
        }

        /* Education Section */
        .education-card {
          background: var(--panel);
          border: 1px solid rgba(94, 234, 212, 0.12);
          border-left: 3px solid var(--signal);
          padding: 24px 28px;
          border-radius: 8px;
          margin-top: 8px;
        }

        .education-title {
          font-size: 1.15rem;
          font-weight: 700;
          margin-bottom: 8px;
          color: var(--text);
        }

        .education-info {
          font-size: 0.95rem;
          color: var(--text-dim);
        }

        /* Languages Section */
        .languages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
          margin-top: 8px;
        }

        .language-item {
          background: var(--panel);
          border: 1px solid rgba(94, 234, 212, 0.1);
          padding: 18px 20px;
          border-radius: 8px;
        }

        .language-name {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: var(--text);
        }

        /* Projects Section */
        .projects-content {
          text-align: left;
          margin-top: 8px;
        }

        .github-link {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          color: var(--text);
          background: var(--panel);
          border: 1px solid rgba(94, 234, 212, 0.15);
          padding: 14px 22px;
          border-radius: 8px;
          margin-top: 12px;
          transition: transform 0.25s ease, border-color 0.25s ease;
        }

        .github-link:hover {
          transform: translateY(-2px);
          border-color: var(--signal);
        }

        .github-icon {
          width: 22px;
          height: 22px;
          filter: brightness(0) saturate(100%) invert(88%) sepia(15%) saturate(495%) hue-rotate(101deg) brightness(101%) contrast(92%);
        }

        /* Contact Section */
        .contact-info {
          font-size: 0.98rem;
          margin: 8px 0;
          color: var(--text-dim);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
        }

        .social-links {
          display: flex;
          gap: 16px;
          margin-top: 24px;
          flex-wrap: wrap;
        }

        .social-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 24px;
          background: transparent;
          border: 1px solid var(--signal);
          color: var(--signal);
          text-decoration: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.9rem;
          transition: background 0.25s ease, color 0.25s ease;
        }

        .social-link:hover {
          background: var(--signal);
          color: var(--ink);
        }

        .social-link:hover .social-icon {
          filter: brightness(0) saturate(100%);
        }

        .social-icon {
          width: 18px;
          height: 18px;
          filter: brightness(0) saturate(100%) invert(88%) sepia(15%) saturate(495%) hue-rotate(101deg) brightness(101%) contrast(92%);
          transition: filter 0.25s ease;
        }

        .blog-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .header-blog-link {
            top: 16px;
            right: 16px;
            padding: 8px 12px;
            font-size: 0.75rem;
          }

          .typing-text {
            font-size: 1.4rem;
          }

          .profile-img {
            width: 130px;
            height: 130px;
          }

          .section-title {
            font-size: 1.4rem;
          }

          .skills-grid {
            grid-template-columns: 1fr;
          }

          .social-links {
            flex-direction: column;
            align-items: stretch;
          }

          .social-link {
            justify-content: center;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .animated-section {
            transition: none;
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <CircuitBackground />
        <a
          href="https://blog.ariyansaadatmand.ir/"
          target="_blank"
          rel="noopener noreferrer"
          className="header-blog-link"
          aria-label="Visit Ariyan Saadatmand's blog"
        >
          <svg className="blog-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M6 2h9l5 5v15H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm8 2H6v16h12V8h-4V4Zm-5 8h6v2H9v-2Zm0 4h6v2H9v-2Zm5-11.5V7h2.5L14 4.5Z"
            />
          </svg>
          Blog
        </a>
        <img
          src={profileImage}
          alt="Ariyan Saadatmand"
          className="profile-img"
        />
        <TypingEffect text="Hi, I'm Ariyan Saadatmand" speed={90} />
        <p className="subtitle">Python / Django Developer</p>
      </header>

      {/* Content */}
      <div className="content">
        {/* About Section */}
        <AnimatedSection label="01 — Profile">
          <h2 className="section-title">About Me</h2>
          <p className="about-text">
            I'm a Computer Engineering student at BIHE, based in Karaj, Iran,
            focused on backend development with Python and Django. I like
            building things end to end — from a normalized database schema
            up through a REST API — and I bring the same structured,
            object-oriented approach to Arduino-based IoT projects as a hobby.
          </p>
        </AnimatedSection>

        {/* Education Section */}
        <AnimatedSection delay={80} label="02 — Education">
          <h2 className="section-title">Education</h2>
          <div className="education-card">
            <div className="education-title">Bachelor of Computer Engineering</div>
            <div className="education-info">BIHE — Bahá'í Institute for Higher Education</div>
            <div className="education-info">2022 — Ongoing</div>
          </div>
        </AnimatedSection>

        {/* Skills Section */}
        <AnimatedSection delay={160} label="03 — Skills">
          <h2 className="section-title">Skills</h2>
          <div className="skills-grid">
            {skills.map((skill, index) => (
              <div key={index} className="skill-item">
                <div className="skill-header">
                  <img src={skill.icon} alt={skill.name} className="skill-icon" />
                  <span className="skill-name">{skill.name}</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Hobbies Section */}
        <AnimatedSection delay={240} label="04 — Beyond Code">
          <h2 className="section-title">Hobbies</h2>
          <ul className="hobbies-list">
            {hobbies.map((hobby, index) => (
              <li key={index} className="hobby-item">
                {hobby}
              </li>
            ))}
          </ul>
        </AnimatedSection>

        {/* Languages Section */}
        <AnimatedSection delay={320} label="05 — Languages">
          <h2 className="section-title">Languages</h2>
          <div className="languages-grid">
            <div className="language-item">
              <div className="language-name">Persian (Native)</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: '100%' }}></div>
              </div>
            </div>
            <div className="language-item">
              <div className="language-name">English (Intermediate)</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: '80%' }}></div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Projects Section */}
        <AnimatedSection delay={400} label="06 — Projects">
          <h2 className="section-title">Projects</h2>
          <div className="projects-content">
            <p className="about-text">All of my projects, including Django REST APIs and IoT/Arduino builds, are on GitHub:</p>
            <a
              href="https://github.com/AriyanSD"
              target="_blank"
              rel="noopener noreferrer"
              className="github-link"
            >
              <img src={githubIcon} alt="GitHub" className="github-icon" />
              <span>View my repositories →</span>
            </a>
          </div>
        </AnimatedSection>

        {/* Contact Section */}
        <AnimatedSection delay={480} label="07 — Contact">
          <h2 className="section-title">Get in Touch</h2>
          <p className="contact-info">ariyan.saadatmand@gmail.com</p>
          <p className="contact-info">+98 910 871 8490</p>
          <div className="social-links">
            <a
              href="https://github.com/AriyanSD"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <img src={githubIcon} alt="GitHub" className="social-icon" />
              GitHub
            </a>
            <a
              href="https://www.linkedin.com/in/ariyan-saadatmand/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <img src={linkedinIcon} alt="LinkedIn" className="social-icon" />
              LinkedIn
            </a>
            <a
              href="https://blog.ariyansaadatmand.ir/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <svg className="blog-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M6 2h9l5 5v15H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm8 2H6v16h12V8h-4V4Zm-5 8h6v2H9v-2Zm0 4h6v2H9v-2Zm5-11.5V7h2.5L14 4.5Z"
                />
              </svg>
              Blog
            </a>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}
